import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";

import { parseLocalDate } from "@/lib/dates";
const fmt = (s) => { const d = parseLocalDate(s); return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"; };

// Person-side partnerships surface (Phase 8): linked orgs + follow-ups + interactions.
// Silently renders nothing if the viewer lacks partnerships access (403).
export default function PersonPartnerships({ kind, id }) {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [fu, setFu] = useState({ due: "", note: "" });

  const load = useCallback(async () => {
    try { const r = await api.get(`/partnerships/person/${kind}/${id}`); setData(r.data); }
    catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);

  const addFollowUp = async () => {
    if (!fu.due) return toast.error("Pick a due date.");
    try { await api.post("/follow-ups", { entity_kind: kind, entity_id: id, due: fu.due, note: fu.note }); toast.success("Follow-up scheduled"); setAdding(false); setFu({ due: "", note: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };
  const complete = async (fid) => {
    const outcome = window.prompt("Outcome / notes (optional):", "");
    if (outcome === null) return;
    try { await api.post(`/follow-ups/${fid}/complete`, { outcome }); toast.success("Marked done"); load(); }
    catch (e) { toast.error("Could not complete"); }
  };

  if (denied || !data) return null;
  const { orgs, follow_ups, interactions } = data;

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="person-partnerships">
      <h3 className="font-zilla text-lg font-semibold mb-3">Partnerships & follow-ups</h3>

      <div className="mb-4">
        <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Linked organizations</div>
        {orgs.length === 0 ? <div className="text-[14px] text-ink/50">Not linked to any organization.</div> : (
          <div className="space-y-1.5" data-testid="person-orgs">
            {orgs.map((o) => (
              <div key={o.id} data-testid={`person-org-${o.id}`} className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2">
                <div className="min-w-0"><span className="font-semibold text-[15px]">{o.name}</span>{o.partner_type && <span className="text-[12px] text-ink/50"> · {o.partner_type}</span>}</div>
                {o.role && <span className="text-[12px] px-2 py-0.5 rounded-full bg-ink/8 shrink-0">{o.role}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[13px] font-semibold text-ink/60">Follow-ups</div>
          <button data-testid="person-add-followup" onClick={() => setAdding((v) => !v)} className="text-dark-olive font-semibold text-[13px] inline-flex items-center gap-1"><Icon name="Plus" className="w-3.5 h-3.5" /> Schedule a follow-up</button>
        </div>
        {adding && (
          <div className="bg-warm-cream border border-ink/10 rounded-lg p-3 mb-2 space-y-2" data-testid="followup-form">
            <div className="flex gap-2 flex-wrap">
              <input type="date" data-testid="followup-due" value={fu.due} onChange={(e) => setFu({ ...fu, due: e.target.value })} className="bg-white border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]" />
              <input data-testid="followup-note" value={fu.note} onChange={(e) => setFu({ ...fu, note: e.target.value })} placeholder="Note (optional)" className="flex-1 min-w-[140px] bg-white border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]" />
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="followup-save" onClick={addFollowUp} className="bg-ink text-cream rounded-lg px-3 py-2 min-h-[40px] text-[13px] font-semibold">Save</button>
              <button disabled title="Coming soon" className="border border-ink/15 text-ink/40 rounded-lg px-3 py-2 min-h-[40px] text-[13px] font-semibold cursor-not-allowed" data-testid="followup-calendar">Schedule to Calendar (coming soon)</button>
            </div>
          </div>
        )}
        {follow_ups.length === 0 ? <div className="text-[14px] text-ink/50">No follow-ups.</div> : (
          <div className="space-y-1.5" data-testid="person-followups">
            {follow_ups.map((f) => (
              <div key={f.fu_id} data-testid={`followup-${f.fu_id}`} className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold">Due {fmt(f.due)}{f.status === "done" && <span className="text-[12px] px-2 py-0.5 rounded-full bg-olive/25 text-dark-olive ml-2">done</span>}</div>
                  {f.note && <div className="text-[13px] text-ink/55">{f.note}</div>}
                  {f.status === "done" && f.outcome && <div className="text-[12px] text-ink/50 italic">Outcome: {f.outcome}</div>}
                </div>
                {f.status !== "done" && <button data-testid={`followup-complete-${f.fu_id}`} onClick={() => complete(f.fu_id)} className="text-dark-olive font-semibold text-[13px] shrink-0">Complete</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {interactions.length > 0 && (
        <div>
          <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Interactions involving this person</div>
          <div className="space-y-1.5" data-testid="person-interactions">
            {interactions.map((it, i) => (
              <div key={i} className="bg-warm-cream border border-ink/10 rounded-lg px-3 py-2">
                <div className="text-[13px] text-ink/55">{fmt(it.at)} · {it.org_name} · <span className="capitalize">{it.kind}</span></div>
                <div className="text-[14px]">{it.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
